import { inject, injectable } from 'inversify';
import type { JwtRepositoryInterface } from '@shared/common/network/domain/repository/jwt.repository';
import { NetworkDependencyTypes } from '@shared/common/network/network.dependency-types';
import type { JwtLocalStorageService } from '@shared/common/network/data/service/jwt-local-storage.service';
import { MissingJwtTokenError } from '@shared/common/network/domain/error/missing-jwt-token.error';

interface GetTokenOptions {
  forceRefresh?: boolean;
}

@injectable()
export class JwtRepository implements JwtRepositoryInterface {
  private static readonly MOUNT_SELECTOR = '#app';
  private static readonly TOKEN_QUERY_PARAM = 'token';
  private inMemoryToken: string | null = null;

  public constructor(
    @inject(NetworkDependencyTypes.JwtLocalStorageService)
    private readonly jwtLocalStorageService: JwtLocalStorageService,
  ) {}

  public hasToken(): boolean {
    return Boolean(
      this.inMemoryToken ||
      this.jwtLocalStorageService.get() ||
      this.readTokenFromDomDataset() ||
      this.readTokenFromUrl(),
    );
  }

  public async getToken(options: GetTokenOptions = {}): Promise<string> {
    const { forceRefresh = false } = options;

    try {
      if (!forceRefresh) {
        const cached = this.inMemoryToken ?? this.jwtLocalStorageService.get();
        if (cached) {
          this.inMemoryToken = cached;
          return cached;
        }
      }

      const domToken = this.readTokenFromDomDataset();
      if (domToken) {
        this.saveToken(domToken);
        return domToken;
      }

      const urlToken = this.readTokenFromUrl();
      if (!urlToken) {
        throw new MissingJwtTokenError();
      }

      this.saveToken(urlToken);
      return urlToken;
    } finally {
      this.clearTokenFromUrl();
    }
  }

  public saveToken(token: string): void {
    this.inMemoryToken = token;
    this.jwtLocalStorageService.save(token);
  }

  public clearToken(): void {
    this.inMemoryToken = null;
    this.jwtLocalStorageService.clear();
    this.clearTokenFromDomDataset();
  }

  private readTokenFromDomDataset(): string | null {
    const mountElement = document.querySelector(JwtRepository.MOUNT_SELECTOR) as HTMLElement | null;

    if (!mountElement) {
      return null;
    }

    return mountElement.dataset.apiToken ?? null;
  }

  private clearTokenFromDomDataset(): void {
    const mountElement = document.querySelector(JwtRepository.MOUNT_SELECTOR) as HTMLElement | null;

    if (!mountElement) {
      return;
    }

    delete mountElement.dataset.apiToken;
  }

  private readTokenFromUrl(): string | null {
    const query = new URLSearchParams(window.location.search);

    return query.get(JwtRepository.TOKEN_QUERY_PARAM);
  }

  private clearTokenFromUrl(): void {
    const url = new URL(window.location.href);

    if (!url.searchParams.has(JwtRepository.TOKEN_QUERY_PARAM)) {
      return;
    }

    url.searchParams.delete(JwtRepository.TOKEN_QUERY_PARAM);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
}
